/**
 * Copyright (c) 2024, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { SimpleIndefiniteProgressDialogComponent } from "app/components/simple-indefinite-progress-dialog/simple-indefinite-progress-dialog.component";
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from "app/components/simple-success-error-dialog-box/simple-success-error-dialog-box.component";
import { ServiceInstance } from "app/models/service-instance";
import { KissPortalValidationProcessLineItem, KissReaderStateDto, KissReaderStateEnum, KissService, VI_LineItem, ValidationInfo, ValidationOrderType, ValidationState } from "app/services/kiss.service";

@Component({
    templateUrl: './kiss-portal-validation.dashboard.component.html',
    styleUrls: ['./kiss-portal-validation.dashboard.component.css']
  })
export class KissPortalValidationDashboardComponent implements OnInit, OnDestroy {

  /** Progress Dialog displayed when performing actions to give the User an indication something is happening... */
  @ViewChild(SimpleIndefiniteProgressDialogComponent) indefiniteProgressDialog: SimpleIndefiniteProgressDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  portal: ServiceInstance;
  state: ValidationInfo;
  readerState: KissReaderStateDto;

  isClearOrderBeingProcessed = false;
  isReaderActionBeingProcessed = false;
  isProcessActionBeingProcessed = false;

  isDestroyed = false;

  inputScannedQuantitiesMap: Map<string, number> = new Map<string, number>();

  isSelectingAllLineItems: boolean = false;

  selectedLineItemsIndex: string[] = [];

  /** Constructor */
  constructor(
    private kissService: KissService,
    private route: ActivatedRoute,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  /**
   * Initializes the component.  
   * Called automatically by the Angular Component Lifecycle.
   */
  ngOnInit(): void {
    console.log(`ngOnInit()`);
    this.route.data.subscribe(
      (data: {
        portal: ServiceInstance,
        state: ValidationInfo,
        readerState: KissReaderStateDto
      }) => {
        console.log(`Obtained Portal: '${data?.portal?.id}/${data?.portal?.name}'`);
        this.portal = data?.portal;

        console.log(`Obtained State: '${JSON.stringify(data?.state)}'`);
        this.state = data?.state;

        console.log(`Obtained Reader State: '${JSON.stringify(data?.readerState)}'`);
        this.readerState = data?.readerState;

        if (this.state.state == ValidationState.EnterOrderInfo) {
          console.log(`Portal is in 'Enter Info' State... Redirecting...`);
          this.router.navigate(['kiss', 'portal-validation', this.portal.id, 'enter-info'], {});
        }
      });

    setTimeout(this.refreshData.bind(this), 1000);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
  }

  private async refreshData(): Promise<void> {
    if (!this.isDestroyed) {
      const state = await this.kissService.getValidationInfo(this.portal.id).toPromise();
      if (JSON.stringify(this.state) != JSON.stringify(state)) {
        this.state = state;
        this.changeDetectorRef.detectChanges();
      }
      
      if (!this.isDestroyed) {
        setTimeout(this.refreshData.bind(this), 1000);
      }
    }
  }

  /**
   * Returns a string representation of the information the User entered for the Order
   * being validated.  Used by the UI to display in the top right of the table. 
   */
  getUserInputForOrder(): string {
    if (this?.state?.order_identity != null) {
      switch (this.state.order_identity.type) {
        case ValidationOrderType.OrderNumber: {
          return `PO / PP #: ${this.state.order_identity.value}`
        }
        case ValidationOrderType.TransportId: {
          return `TransID (Container ID): ${this.state.order_identity.value}`
        }
        case ValidationOrderType.BillOfLading: {
          return `Bill of Lading: ${this.state.order_identity.value}`
        }
        case ValidationOrderType.VendorInvoice: {
          return `Vendor Invoice: ${this.state.order_identity.value}`
        }
        case ValidationOrderType.InboundDelivery: {
          return `Inbound Delivery (ASN): ${this.state.order_identity.value}`
        }
        default: {
          return 'Unknown Order Type';
        }
      }
    } else {
      return 'Unknown Order';
    }
  }

  onClearOrderClicked(): void {
    if (!this.isClearOrderBeingProcessed) {
      this.isClearOrderBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Clearing Order...");
      this.clearOrder();
    }
  }

  private async clearOrder(): Promise<void> {
    let state: ValidationInfo = null;
    try {
      state = await this.kissService.clearOrderInfo(this.portal.id).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isClearOrderBeingProcessed = false;
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.router.navigate(['kiss', 'portal-validation', this.portal.id, 'enter-info'], {});
  }





  isReaderStarted(): boolean {
    return this.readerState?.reader_state == KissReaderStateEnum.Started;
  }

  onStartReaderClicked(): void {
    console.log(`onStartReaderClicked()`);
    if (!this.isReaderActionBeingProcessed) {
      this.isReaderActionBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Starting Reader...");
      this.startReader();
    }
  }

  onStopReaderClicked(): void {
    console.log(`onStopReaderClicked()`);
    if (!this.isReaderActionBeingProcessed) {
      this.isReaderActionBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Stopping Reader...");
      this.stopReader();
    }
  }

  private async startReader(): Promise<void> {
    console.log(`startReader()`);

    try {
      await this.kissService.startReader(this.portal.id).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isReaderActionBeingProcessed = false;
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.readerState.reader_state = KissReaderStateEnum.Started;
    this.isReaderActionBeingProcessed = false;
  }

  private async stopReader(): Promise<void> {
    console.log(`stopReader()`);

    try {
      await this.kissService.stopReader(this.portal.id).toPromise();
    } catch (error) {
      this.indefiniteProgressDialog.hide();
      this.isReaderActionBeingProcessed = false;
      this.handleError(error);
      return;
    }

    this.indefiniteProgressDialog.hide();
    this.readerState.reader_state = KissReaderStateEnum.Stopped;
    this.isReaderActionBeingProcessed = false;
  }





  getInputScannedQuantityFormattedForDisplay(lineItem: VI_LineItem): string {
    if (this.inputScannedQuantitiesMap.has(lineItem.uuid)) {
      const value = this.inputScannedQuantitiesMap.get(lineItem.uuid);
      if (value > 0) {
        return `[${value}]`;
      }
    }
    return "";
  }

  getInputScannedQuantity(lineItem: VI_LineItem): number {
    if (this.inputScannedQuantitiesMap.has(lineItem.uuid)) {
      const value = this.inputScannedQuantitiesMap.get(lineItem.uuid);
      if (value > 0) {
        return value;
      } else {
        return lineItem.scanned_quantity;
      }
    } else {
      return lineItem.scanned_quantity;
    }
  }

  getActualScannedQuantityFormattedForDisplay(lineItem: VI_LineItem): string {
    return `[${lineItem.scanned_quantity}]`
  } 

  onInputScannedQuantityChanged(lineItem: VI_LineItem, newValue: number): void {
    if (newValue === null || newValue === undefined) {
      this.inputScannedQuantitiesMap.delete(lineItem.uuid);
    } else {
      this.inputScannedQuantitiesMap.set(lineItem.uuid, newValue);
    }
  }




  selectAllLineItems(select: boolean): void {
    console.log(`selectAllLineItems(${select})`);
    if (!select) {
      while (this.selectedLineItemsIndex.length > 0) {
        this.selectedLineItemsIndex.pop();
      }
    } else {
      for (let li of this.state?.line_items) {
        if (!this.selectedLineItemsIndex.includes(li.uuid)) {
          this.selectedLineItemsIndex.push(li.uuid);
        }
      }
    }
  }

  isAllLineItemsSelected(): boolean {
    return this.selectedLineItemsIndex.length == this.state?.line_items?.length;
  }

  onLineItemToggled(lineItem: VI_LineItem, select: boolean): void {
    console.log(`onLineItemToggled(${JSON.stringify(lineItem)}, ${select})`);
    if (select) {
      if (!this.selectedLineItemsIndex.includes(lineItem.uuid)) {
        this.selectedLineItemsIndex.push(lineItem.uuid);
      }
    } else {
      const i = this.selectedLineItemsIndex.findIndex(selectedUuid => selectedUuid == lineItem.uuid);
      if (i >= 0) {
        this.selectedLineItemsIndex.splice(i, 1);
      }
    }
  }

  isLineItemSelected(lineItem: VI_LineItem): boolean {
    return this.selectedLineItemsIndex.includes(lineItem.uuid);
  }




  /**
   * Called by UI whent he "Process" button is clicked.
   * Starts the "Process" process if it isn't already being done.
   */
  onProcessClicked(): void {
    if (!this.isProcessActionBeingProcessed) {
      this.isProcessActionBeingProcessed = true;
      this.indefiniteProgressDialog.show("Processing", "Updating Order Information...");
      this.processOrder();
    }
  }

  /**
   * Processes the Order information from the UI by calling the API with the Line Items 
   * selected to send and any override quantities that were specified
   */
  private async processOrder(): Promise<void> {
    try {
      const lineItems: KissPortalValidationProcessLineItem[] = [];
      for (let i = 0; i < this.state?.line_items?.length; i ++) {
        const lineItem = this.state.line_items[i];
        if (this.selectedLineItemsIndex.includes(lineItem.uuid)) {
          const scannedQuantityOverride = this.inputScannedQuantitiesMap.has(lineItem.uuid)? this.inputScannedQuantitiesMap.get(lineItem.uuid) : null;
          lineItems.push({ uuid: lineItem.uuid, scanned_quantity_override: scannedQuantityOverride} as KissPortalValidationProcessLineItem);
        }
      }

      if (lineItems.length == 0) {
        this.indefiniteProgressDialog.hide();
        this.handleError({ error: "No Line Items have been selected." });
        return;
      }

      try {
        await this.kissService.processValidation(this.portal.id, lineItems).toPromise();
      } catch (error) {
        this.indefiniteProgressDialog.hide();
        this.handleError(error);
        return;
      }

      this.indefiniteProgressDialog.hide();
      
      // Return the User to the "Enter Order Info" screen
      this.router.navigate(['kiss', 'portal-validation', this.portal.id, 'enter-info'], {});
      return;
    } finally {
      this.isProcessActionBeingProcessed = false;
    }
  }



  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being in error. (RED)
   */
  isErrorRow(lineItem: VI_LineItem): boolean {
    return lineItem?.balance_quantity < 0 && lineItem?.required_quantity > 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighted as
   * being unknown. (GRAY)
   */
  isUnknownRow(lineItem: VI_LineItem): boolean {
    return lineItem?.balance_quantity < 0 && lineItem?.required_quantity == 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * being completed successfully. (GREEN)
   */
  isSuccessRow(lineItem: VI_LineItem): boolean {
    return lineItem?.balance_quantity == 0;
  }

  /**
   * Used by UI to determine if the Row / Line Item should be highlighed as 
   * partially fulfilled. (YELLOW)
   */
  isPartialRow(lineItem: VI_LineItem): boolean {
    return (lineItem?.scanned_quantity > 0 || lineItem?.completed_quantity > 0) && lineItem?.balance_quantity > 0;
  }





  /**
   * Called to handle errors when calling APIs.  
   * Currently this prints them out to the console and displays an alert to the User.
   */
  handleError(error: any): void {
    console.error(`Error detected: ${error}`);
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    }
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }

}
